/**
 * Восстановление прошлых ставок (13.10, владелец: «смена ставок меняла прошлые зарплаты — сильнейший бред,
 * оценить масштаб и восстановить цифры»). Только ИО. План посчитан офлайн (копия базы, дамп V1, расчётки),
 * сервер (routes/rate-restore.ts) сверяет его с живой базой: «готово» / «уже применено» / «изменилось».
 * Применяется только группа «без решений»; группы решений владельца — после его ответа. Откат — одной кнопкой.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { ConfirmModal } from "../../components/shared";
import css from "./rateRestore.module.css";

interface Group { title: string; rows: number; ready: number; applied: number; changed: number; now: number; after: number }
interface Resp {
  generated_at: string | null;
  groups: Record<string, Group>;
  staff: Record<string, { name: string; months: Record<string, { now: number; after: number; games: number }> }>;
  changed: { id: number; staff: string; date: string; quest: string; plan_to: number; live_rate: number | null }[];
  questions: string[];
  applied_log: number;
  /** Дозаливка: игры и смены, которых в системе нет (решения владельца 13.10). */
  inserts?: { title: string; staff: string; date: string; kind: string; state: "ready" | "applied" | "changed"; amount: number }[];
}

const rub = (n: number) => `${n < 0 ? "−" : n > 0 ? "+" : ""}${Math.abs(Math.round(n)).toLocaleString("ru-RU")} ₽`;
const sum = (n: number) => `${Math.round(n).toLocaleString("ru-RU")} ₽`;
const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const mon = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym} ${ym.slice(2, 4)}`;

export function RateRestorePanel() {
  const [data, setData] = useState<Resp | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<"apply" | "rollback" | null>(null);
  const [toast, setToast] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setErr("");
    api<Resp>("/api/eo/rate-restore").then(setData).catch((e: unknown) => setErr(e instanceof Error ? e.message : "Не удалось загрузить"));
  }, []);
  useEffect(load, [load]);

  const run = async (kind: "apply" | "rollback") => {
    setAsk(null); setBusy(true);
    try {
      const r = kind === "apply"
        ? await api<{ applied: number; delta: number }>("/api/eo/rate-restore/apply", { method: "POST", body: { groups: ["auto"] } })
        : await api<{ rolled_back: number }>("/api/eo/rate-restore/rollback", { method: "POST", body: {} });
      setToast("applied" in r ? `Применено строк: ${r.applied}, разница ${rub(r.delta)}` : `Отменено строк: ${r.rolled_back}`);
      load();
    } catch (e) { setToast(e instanceof Error ? e.message : "Ошибка"); }
    setBusy(false);
  };

  if (err) return <div className={css.card}><div className={css.err}>{err}</div></div>;
  if (!data) return <div className={css.card}><div className={css.muted}>Загружаю план восстановления…</div></div>;
  const auto = data.groups.auto;
  const holds = Object.entries(data.groups).filter(([k]) => k !== "auto");
  const people = Object.entries(data.staff).map(([id, s]) => {
    const now = Object.values(s.months).reduce((a, m) => a + m.now, 0), after = Object.values(s.months).reduce((a, m) => a + m.after, 0);
    return { id, name: s.name, months: s.months, delta: after - now };
  }).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Восстановление прошлых ставок</span>
        {data.generated_at ? <span className={css.muted}>план от {data.generated_at.slice(0, 10)}</span> : null}
      </div>
      <p className={css.lead}>Смена грейдов и ставок с 26.08 пересчитывала уже сыгранные игры. План ставит каждой игре ставку на день игры (по старой системе V1 и отправленным расчёткам). Плюс — «после» больше, чем «сейчас».</p>
      {!auto || auto.rows === 0 ? <div className={css.muted}>План ещё не загружен в систему.</div> : (
        <>
          <div className={css.totals}>
            <div><b>{auto.ready}</b><span>готово к применению</span></div>
            <div><b>{auto.applied}</b><span>уже применено</span></div>
            <div><b>{auto.changed}</b><span>изменились после 13.10 — не трогаем</span></div>
            <div><b className={auto.after - auto.now >= 0 ? css.plus : css.minus}>{rub(auto.after - auto.now)}</b><span>{sum(auto.now)} → {sum(auto.after)}</span></div>
          </div>
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead><tr><th>Сотрудник</th><th>По месяцам (после − сейчас)</th><th>Итого</th></tr></thead>
              <tbody>{people.map((p) => (
                <tr key={p.id}><td>{p.name}</td>
                  <td className={css.months}>{Object.entries(p.months).sort().map(([ym, m]) => <span key={ym}>{mon(ym)} {rub(m.after - m.now)}</span>)}</td>
                  <td className={p.delta >= 0 ? css.plus : css.minus}>{rub(p.delta)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className={css.actions}>
            <button type="button" className={css.go} disabled={busy || auto.ready === 0} onClick={() => setAsk("apply")}>Применить ({auto.ready})</button>
            {data.applied_log > 0 ? <button type="button" className={css.undo} disabled={busy} onClick={() => setAsk("rollback")}>Отменить применённое ({data.applied_log})</button> : null}
          </div>
        </>
      )}
      {data.inserts?.length ? (
        <div className={css.block}>
          <div className={css.sub}>Дозалить — этого в системе нет ({data.inserts.filter((x) => x.state === "ready").length} готово)</div>
          {data.inserts.map((x, i) => (
            <div key={i} className={css.hold}><span>{x.date.slice(8, 10)}.{x.date.slice(5, 7)} · {x.staff} · {x.kind === "hall" ? "смена админа зала" : "игра"}</span>
              <span className={x.state === "changed" ? css.minus : css.muted}>{x.state === "applied" ? "уже есть" : x.state === "changed" ? "слот занят" : sum(x.amount)}</span></div>
          ))}
        </div>
      ) : null}
      {holds.length ? (
        <div className={css.block}>
          <div className={css.sub}>Ждут решения владельца — не применяются</div>
          {holds.map(([k, g]) => <div key={k} className={css.hold}><span>{g.title}</span><span className={css.muted}>{g.rows} игр · сейчас {sum(g.now)}</span></div>)}
        </div>
      ) : null}
      {data.questions.length ? <div className={css.block}><div className={css.sub}>Вопросы</div>{data.questions.map((q, i) => <div key={i} className={css.q}>{q}</div>)}</div> : null}
      {data.changed.length ? (
        <div className={css.block}>
          <button type="button" className={css.toggle} onClick={() => setOpen((v) => !v)}>{open ? "▾" : "▸"} Изменились после 13.10 ({auto?.changed ?? data.changed.length})</button>
          {open ? data.changed.map((c) => <div key={c.id} className={css.q}>{c.date} · {c.staff} · {c.quest}: в плане {c.plan_to} ₽, сейчас {c.live_rate ?? "—"}</div>) : null}
        </div>
      ) : null}
      {toast ? <div className={css.toast} role="status">{toast}</div> : null}
      {ask ? (
        <ConfirmModal tone="warn" title={ask === "apply" ? "Применить восстановление?" : "Отменить применённое?"}
          actionLabel={ask === "apply" ? "Применить" : "Отменить"} onAction={() => void run(ask)} onCancel={() => setAsk(null)}>
          {ask === "apply"
            ? `Ставки ${auto?.ready ?? 0} прошлых игр станут ставками на день игры. Зарплаты за прошлые месяцы изменятся на ${rub((auto?.after ?? 0) - (auto?.now ?? 0))}. Отменить можно этой же панелью.`
            : "Ставки вернутся к значениям до применения (где их с тех пор не меняли)."}
        </ConfirmModal>
      ) : null}
    </div>
  );
}
